import { api } from './apiClient';
import {
  Account,
  AccountType,
  AccountStats,
  AccountsWithStats,
  JournalEntry,
  JournalEntryStatus,
  JournalEntryFilters,
  CreateJournalEntryData,
  EnhancedGeneralLedger,
  MultiAccountLedger,
  OpeningBalance,
  SetOpeningBalanceData,
  FinancialPeriod,
  ScheduleIIIBalanceSheet,
  ScheduleIIIProfitLoss,
  ScheduleIIIMapping,
  TrialBalance,
  ComparativeTrialBalance,
  CashFlowStatement,
  GSTR1Summary,
  GSTR1B2BDetail,
  GSTR1HSNItem,
  GSTR3BSummary,
  GSTInputOutputSummary,
  RecordGSTTransactionData,
  GSTTransaction,
  TDSSummary,
  TDSQuarterlyData,
  TDSPayableReport,
  TDSDeduction,
  TDSChallan,
  RecordTDSDeductionData,
  RecordTDSChallanData,
  AgeingReport,
  DayBook,
  CostCenterReport,
  BankReconciliation,
  CreateBankReconciliationData,
  ExportReportData,
} from '../types/finance';

// Re-export types for backward compatibility
export type {
  AccountType,
  JournalEntryStatus,
  GSTType,
  Account,
  AccountStats,
  AccountsWithStats,
  JournalEntry,
  JournalEntryLine,
  JournalEntryFilters,
  CreateJournalEntryData,
  CreateJournalEntryLine,
} from '../types/finance';

// Legacy types (for backward compatibility)
interface Transaction {
  id: string;
  type: 'income' | 'expense';
  amount: number;
  description: string;
  category: string;
  date: string;
  account: string;
  status: 'pending' | 'completed' | 'cancelled';
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  amount: number;
  dueDate: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  items: InvoiceItem[];
}

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface FinancialReport {
  period: string;
  revenue: number;
  expenses: number;
  profit: number;
  profitMargin: number;
}

// API Response wrapper types
interface AccountsResponse {
  accounts: Account[];
  stats: AccountStats;
  count: number;
}

interface JournalEntriesResponse {
  journalEntries: JournalEntry[];
  count: number;
}

interface JournalEntryResponse {
  journalEntry: JournalEntry;
}

/**
 * MySQL DECIMAL columns arrive over the wire as *strings* (mysql2 preserves
 * precision by not casting them to JS floats). The finance types declare these
 * fields as `number`, and consumers rely on that — a page that does
 * `entries.reduce((s, e) => s + e.total_debit, 0)` gets string concatenation
 * ("0" + "1500.00" + ...) instead of a sum, and the later arithmetic on that
 * garbage yields NaN (the "Off by ₹NaN" on the Journal Entries page).
 *
 * Coerce at the service boundary so the data actually matches its declared
 * type for every consumer, not just the one that happened to surface the bug.
 * A null/blank/garbage value becomes 0 rather than NaN — a journal entry with a
 * missing total is a zero on that side, never a poison value that breaks a sum.
 */
const toNum = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeJournalEntry = (entry: JournalEntry): JournalEntry => ({
  ...entry,
  total_debit: toNum(entry.total_debit),
  total_credit: toNum(entry.total_credit),
  lines: entry.lines?.map((line) => ({
    ...line,
    debit_amount: toNum(line.debit_amount),
    credit_amount: toNum(line.credit_amount),
    gst_amount: line.gst_amount === undefined ? undefined : toNum(line.gst_amount),
  })),
});

// =============================================
// TDS RESPONSE ADAPTERS
//
// The TDS report endpoints return grouped / snake_case rows shaped by the
// SQL, not the camelCase view-model the TDS report pages consume (the page
// reads `.sections` / `.totals` / per-deductee `items`). These helpers adapt
// the raw responses into the `types/finance` contracts so the pages render
// real data instead of crashing on undefined fields.
// =============================================

const round2 = (n: number): number => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;

/** "FY2026-27" -> 2026 (the April-start calendar year). */
const fyStartYear = (fy: string): number => parseInt((fy || '').replace(/[^0-9]/g, '').slice(0, 4), 10) || new Date().getFullYear();

/** Inclusive [from, to] ISO date range for a quarter within a financial year. */
const quarterRange = (fy: string, quarter: string): { from: string; to: string } => {
  const y = fyStartYear(fy);
  switch (quarter) {
    case 'Q1': return { from: `${y}-04-01`, to: `${y}-06-30` };
    case 'Q2': return { from: `${y}-07-01`, to: `${y}-09-30` };
    case 'Q3': return { from: `${y}-10-01`, to: `${y}-12-31` };
    default: return { from: `${y + 1}-01-01`, to: `${y + 1}-03-31` }; // Q4
  }
};

const fmtDMY = (d: Date): string =>
  d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

/** Statutory TDS deposit due date = 7th of the month following the deduction
 *  month (Apr–Feb). March deductions are due 30 April. */
const depositDueForDeduction = (deductionDate: string): Date => {
  const d = new Date(deductionDate);
  if (Number.isNaN(d.getTime())) return new Date();
  if (d.getMonth() === 2) return new Date(d.getFullYear(), 3, 30); // March -> 30 Apr
  return new Date(d.getFullYear(), d.getMonth() + 1, 7);
};

/** Quarter-level deposit / return-filing due dates, for the 26Q header. */
const quarterDueDates = (fy: string, quarter: string): { depositDueDate: string; dueDate: string } => {
  const y = fyStartYear(fy);
  const map: Record<string, { deposit: Date; filing: Date }> = {
    Q1: { deposit: new Date(y, 6, 7), filing: new Date(y, 6, 31) },
    Q2: { deposit: new Date(y, 9, 7), filing: new Date(y, 9, 31) },
    Q3: { deposit: new Date(y + 1, 0, 7), filing: new Date(y + 1, 0, 31) },
    Q4: { deposit: new Date(y + 1, 3, 30), filing: new Date(y + 1, 4, 31) },
  };
  const m = map[quarter] || map.Q1;
  return { depositDueDate: fmtDMY(m.deposit), dueDate: fmtDMY(m.filing) };
};

// =============================================
// AGEING ADAPTER
//
// The ageing endpoints return `{ asOnDate, customers|vendors:[{ name, invoices,
// buckets, total }], totals }` — but the Ageing report page consumes the
// `AgeingReport` view-model (summary + flat buckets + flat items). This maps
// one to the other so the page renders real data instead of crashing on
// `undefined.totalOutstanding`.
// =============================================

const AGE_BUCKETS: { label: string; minDays: number; maxDays: number | null; key: string }[] = [
  { label: 'Current (0-30)', minDays: 0, maxDays: 30, key: 'current' },
  { label: '31-60 days', minDays: 31, maxDays: 60, key: 'days31_60' },
  { label: '61-90 days', minDays: 61, maxDays: 90, key: 'days61_90' },
  { label: '91-120 days', minDays: 91, maxDays: 120, key: 'days91_120' },
  { label: '120+ days', minDays: 121, maxDays: null, key: 'over120' },
];
const ageBucketForDays = (d: number): string =>
  d <= 30 ? 'Current (0-30)' : d <= 60 ? '31-60 days' : d <= 90 ? '61-90 days' : d <= 120 ? '91-120 days' : '120+ days';

const mapAgeing = (raw: any, reportType: 'RECEIVABLES' | 'PAYABLES'): AgeingReport => {
  const parties: any[] = raw?.customers || raw?.vendors || [];
  const partyType: 'CUSTOMER' | 'VENDOR' = reportType === 'RECEIVABLES' ? 'CUSTOMER' : 'VENDOR';
  const items = parties.flatMap((p) => {
    const partyName = p.customerName || p.vendorName || p.partyName || '';
    const partyId = p.customerId || p.vendorId || p.partyId || 0;
    return (p.invoices || []).map((inv: any) => {
      const days = Math.max(0, toNum(inv.days_overdue ?? inv.daysOverdue));
      return {
        partyId, partyName, partyType,
        invoiceNumber: inv.invoice_number ?? inv.invoiceNumber,
        invoiceDate: inv.invoice_date ?? inv.invoiceDate,
        dueDate: inv.due_date ?? inv.dueDate,
        amount: toNum(inv.outstanding),
        daysOutstanding: days,
        bucket: ageBucketForDays(days),
      };
    });
  });
  const t = raw?.totals || {};
  const totalOutstanding = toNum(t.total) || items.reduce((s, i) => s + i.amount, 0);
  const buckets = AGE_BUCKETS.map((b) => {
    const amount = toNum(t[b.key]);
    return {
      label: b.label, minDays: b.minDays, maxDays: b.maxDays, amount,
      count: items.filter((i) => i.bucket === b.label).length,
      percentage: totalOutstanding > 0 ? (amount / totalOutstanding) * 100 : 0,
    };
  });
  const totalItems = items.length;
  const averageDaysOutstanding = totalItems ? items.reduce((s, i) => s + i.daysOutstanding, 0) / totalItems : 0;
  return { reportType, asOnDate: raw?.asOnDate || '', buckets, items, summary: { totalOutstanding, totalItems, averageDaysOutstanding } };
};

// =============================================
// GST ADAPTERS
//
// The GST report endpoints return raw SQL aggregates (snake_case, and B2C /
// outward as ARRAYS), but the GST report page consumes object view-models
// (`summary.b2b.totalTaxableValue`, `.totalLiability.cgst`,
// `.outwardSupplies.taxableValue`, `.output.total`, …). Without these mappers
// the page reads `undefined.x` and crashes (same class as the ageing bug).
// =============================================

const sumBy = (arr: any[], k: string): number => (arr || []).reduce((s, r) => s + toNum(r[k]), 0);
const ZERO4 = { igst: 0, cgst: 0, sgst: 0, cess: 0 };

const mapGstr1Summary = (raw: any, returnPeriod: string): GSTR1Summary => {
  const b0 = raw?.b2b || {};
  const b2cl: any[] = raw?.b2cl || [];
  const b2cs: any[] = raw?.b2cs || [];
  const b2bTax = { igst: toNum(b0.igst), cgst: toNum(b0.cgst), sgst: toNum(b0.sgst), cess: toNum(b0.cess) };
  const igst = b2bTax.igst + sumBy(b2cl, 'igst');
  const cgst = b2bTax.cgst + sumBy(b2cs, 'cgst');
  const sgst = b2bTax.sgst + sumBy(b2cs, 'sgst');
  const cess = b2bTax.cess + sumBy(b2cl, 'cess') + sumBy(b2cs, 'cess');
  return {
    returnPeriod: raw?.returnPeriod || returnPeriod,
    b2b: { invoiceCount: toNum(b0.invoice_count), totalTaxableValue: toNum(b0.taxable_value), totalIGST: b2bTax.igst, totalCGST: b2bTax.cgst, totalSGST: b2bTax.sgst, totalCess: b2bTax.cess },
    b2cLarge: { invoiceCount: sumBy(b2cl, 'invoice_count'), totalTaxableValue: sumBy(b2cl, 'taxable_value'), totalIGST: sumBy(b2cl, 'igst') },
    b2cSmall: { totalTaxableValue: sumBy(b2cs, 'taxable_value'), totalCGST: sumBy(b2cs, 'cgst'), totalSGST: sumBy(b2cs, 'sgst') },
    exports: { invoiceCount: 0, totalTaxableValue: 0, totalIGST: 0 },
    hsnSummary: [],
    documentSummary: {
      invoices: { from: '', to: '', total: 0, cancelled: 0 },
      debitNotes: { from: '', to: '', total: 0, cancelled: 0 },
      creditNotes: { from: '', to: '', total: 0, cancelled: 0 },
    },
    totalLiability: { igst: round2(igst), cgst: round2(cgst), sgst: round2(sgst), cess: round2(cess), total: round2(igst + cgst + sgst + cess) },
  };
};

const mapGstr3bSummary = (raw: any, returnPeriod: string): GSTR3BSummary => {
  const outward: any[] = raw?.outwardSupplies || [];
  const out = { taxableValue: sumBy(outward, 'taxable_value'), igst: sumBy(outward, 'igst'), cgst: sumBy(outward, 'cgst'), sgst: sumBy(outward, 'sgst'), cess: sumBy(outward, 'cess') };
  const itc = raw?.totalITC || ZERO4;
  const net = raw?.netPayable || ZERO4;
  const itcV = { igst: toNum(itc.igst), cgst: toNum(itc.cgst), sgst: toNum(itc.sgst), cess: toNum(itc.cess) };
  return {
    returnPeriod: raw?.returnPeriod || returnPeriod,
    outwardSupplies: { taxableValue: round2(out.taxableValue), igst: round2(out.igst), cgst: round2(out.cgst), sgst: round2(out.sgst), cess: round2(out.cess) },
    inwardSuppliesReverseCharge: { taxableValue: 0, ...ZERO4 },
    itcAvailable: itcV,
    itcReversed: { ...ZERO4 },
    netItc: itcV,
    taxPayable: { igst: toNum(net.igst), cgst: toNum(net.cgst), sgst: toNum(net.sgst), cess: toNum(net.cess), total: toNum(raw?.totalNetPayable) },
    interestPayable: 0,
    lateFeesPayable: 0,
  };
};

const mapGstInputOutput = (raw: any, fromDate: string, toDate: string): GSTInputOutputSummary => {
  const agg = (arr: any[]) => {
    const o = { cgst: sumBy(arr, 'cgst'), sgst: sumBy(arr, 'sgst'), igst: sumBy(arr, 'igst'), cess: sumBy(arr, 'cess') };
    return { cgst: round2(o.cgst), sgst: round2(o.sgst), igst: round2(o.igst), cess: round2(o.cess), total: round2(o.cgst + o.sgst + o.igst + o.cess) };
  };
  const output = agg(raw?.output || []);
  const input = agg(raw?.input || []);
  return {
    period: { fromDate, toDate },
    output, input,
    netPayable: {
      cgst: round2(output.cgst - input.cgst), sgst: round2(output.sgst - input.sgst),
      igst: round2(output.igst - input.igst), cess: round2(output.cess - input.cess),
      total: round2(output.total - input.total),
    },
  };
};

// Bank reconciliation summary: backend returns { reconciledCount,
// unreconciledCount, reconciledAmount, unreconciledAmount }, but the page reads
// { totalItems, matchedItems, unmatchedItems, pendingItems }. Alias them so the
// chips show real counts instead of "undefined".
const normalizeRecon = (r: any): any => {
  if (!r || typeof r !== 'object') return r;
  const s = r.summary || {};
  const matched = toNum(s.matchedItems ?? s.reconciledCount);
  const unmatched = toNum(s.unmatchedItems ?? s.unreconciledCount);
  const total = toNum(s.totalItems) || (matched + unmatched) || (Array.isArray(r.items) ? r.items.length : 0);
  return {
    ...r,
    summary: {
      ...s,
      totalItems: total, matchedItems: matched, unmatchedItems: unmatched,
      pendingItems: toNum(s.pendingItems ?? s.unreconciledCount),
    },
  };
};

const mapGstr1B2B = (rows: any[]): GSTR1B2BDetail[] => (rows || []).map((r) => ({
  ...r,
  partyGstin: r.party_gstin, partyName: r.party_name, invoiceNumber: r.invoice_number,
  invoiceDate: r.invoice_date, placeOfSupply: r.place_of_supply,
  taxableValue: toNum(r.taxable_value), igstAmount: toNum(r.igst_amount), cgstAmount: toNum(r.cgst_amount),
  sgstAmount: toNum(r.sgst_amount), cessAmount: toNum(r.cess_amount), totalAmount: toNum(r.total_amount),
})) as GSTR1B2BDetail[];

const mapComparativeTB = (raw: any): ComparativeTrialBalance => {
  const accounts: any[] = raw?.accounts || [];
  const t = raw?.totals || {};
  const buildPeriod = (asOnDate: string, debitKey: string, creditKey: string): TrialBalance => ({
    asOnDate: asOnDate || '',
    financialYear: raw?.financialYear || '',
    items: accounts.map((a) => ({
      account_id: a.id ?? a.account_id,
      account_code: a.account_code,
      account_name: a.account_name,
      account_type: a.account_type || 'ASSET',
      opening_debit: 0, opening_credit: 0, period_debit: 0, period_credit: 0,
      closing_debit: toNum(a[debitKey]), closing_credit: toNum(a[creditKey]),
    })),
    totals: {
      openingDebit: 0, openingCredit: 0, periodDebit: 0, periodCredit: 0,
      closingDebit: toNum(t[debitKey]), closingCredit: toNum(t[creditKey]),
    },
  });
  return {
    currentPeriod: buildPeriod(raw?.asOnDate, 'current_debit', 'current_credit'),
    previousPeriod: buildPeriod(raw?.comparativeDate, 'comparative_debit', 'comparative_credit'),
    variance: accounts.map((a) => {
      const prev = toNum(a.comparative_balance);
      const varc = toNum(a.variance);
      return {
        account_id: a.id ?? a.account_id, account_code: a.account_code, account_name: a.account_name,
        currentBalance: toNum(a.current_balance), previousBalance: prev, variance: varc,
        variancePercent: prev ? round2((varc / Math.abs(prev)) * 100) : 0,
      };
    }),
  };
};

const mapGstr1Hsn = (rows: any[]): GSTR1HSNItem[] => (rows || []).map((r) => ({
  ...r,
  hsnCode: r.hsn_code, description: r.description, uqc: r.uqc,
  totalQuantity: toNum(r.total_quantity), totalValue: toNum(r.total_value), taxableValue: toNum(r.taxable_value),
  igstAmount: toNum(r.igst ?? r.igst_amount), cgstAmount: toNum(r.cgst ?? r.cgst_amount),
  sgstAmount: toNum(r.sgst ?? r.sgst_amount), cessAmount: toNum(r.cess ?? r.cess_amount),
})) as GSTR1HSNItem[];

// =============================================
// FINANCE SERVICE
// =============================================

export const financeService = {
  // =============================================
  // CHART OF ACCOUNTS
  // =============================================

  getAccounts: async (filters?: { accountType?: AccountType; isActive?: boolean }): Promise<AccountsWithStats> => {
    const params = new URLSearchParams();
    if (filters?.accountType) params.append('accountType', filters.accountType);
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));

    const response = await api.get<AccountsResponse>(
      `/finance/accounts${params.toString() ? `?${params.toString()}` : ''}`
    );
    return {
      accounts: response.accounts,
      stats: response.stats,
      count: response.count
    };
  },

  getAccountHierarchy: async (): Promise<Account[]> => {
    const response = await api.get<{ hierarchy: Account[] }>('/finance/accounts/hierarchy');
    return response.hierarchy;
  },

  getAccountById: async (id: number): Promise<Account> => {
    const response = await api.get<{ account: Account }>(`/finance/accounts/${id}`);
    return response.account;
  },

  createAccount: async (account: Omit<Account, 'id' | 'created_at' | 'updated_at'>): Promise<Account> => {
    const response = await api.post<{ account: Account }>('/finance/accounts', account);
    return response.account;
  },

  updateAccount: async (id: number, account: Partial<Account>): Promise<Account> => {
    const response = await api.put<{ account: Account }>(`/finance/accounts/${id}`, account);
    return response.account;
  },

  deleteAccount: async (id: number): Promise<void> => {
    await api.delete(`/finance/accounts/${id}`);
  },

  // =============================================
  // SCHEDULE III MAPPING
  // =============================================

  getScheduleIIIMapping: async (accountId: number): Promise<ScheduleIIIMapping | null> => {
    const response = await api.get<{ mapping: ScheduleIIIMapping | null }>(
      `/finance/accounts/${accountId}/schedule-iii-mapping`
    );
    return response.mapping;
  },

  setScheduleIIIMapping: async (accountId: number, categoryId: number): Promise<ScheduleIIIMapping> => {
    const response = await api.post<{ mapping: ScheduleIIIMapping }>(
      `/finance/accounts/${accountId}/schedule-iii-mapping`,
      { categoryId }
    );
    return response.mapping;
  },

  // =============================================
  // JOURNAL ENTRIES
  // =============================================

  getJournalEntries: async (filters?: JournalEntryFilters): Promise<JournalEntry[]> => {
    const params = new URLSearchParams();
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.financialYear) params.append('financialYear', filters.financialYear);

    const response = await api.get<JournalEntriesResponse>(
      `/finance/journal-entries${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.journalEntries.map(normalizeJournalEntry);
  },

  getJournalEntryById: async (id: number): Promise<JournalEntry> => {
    const response = await api.get<JournalEntryResponse>(`/finance/journal-entries/${id}`);
    return normalizeJournalEntry(response.journalEntry);
  },

  createJournalEntry: async (data: CreateJournalEntryData): Promise<JournalEntry> => {
    const response = await api.post<JournalEntryResponse>('/finance/journal-entries', data);
    return normalizeJournalEntry(response.journalEntry);
  },

  updateJournalEntryStatus: async (id: number, status: 'POSTED' | 'REVERSED'): Promise<JournalEntry> => {
    const response = await api.patch<JournalEntryResponse>(
      `/finance/journal-entries/${id}/status`,
      { status }
    );
    return normalizeJournalEntry(response.journalEntry);
  },

  // =============================================
  // ENHANCED GENERAL LEDGER
  // =============================================

  getEnhancedGeneralLedger: async (
    accountId: number,
    fromDate: string,
    toDate: string,
    searchTerm?: string
  ): Promise<EnhancedGeneralLedger> => {
    const params = new URLSearchParams();
    params.append('fromDate', fromDate);
    params.append('toDate', toDate);
    if (searchTerm) params.append('search', searchTerm);

    const response = await api.get<any>(
      `/finance/general-ledger/${accountId}/enhanced?${params.toString()}`
    );

    // The backend now returns display-ready typed balances (opening/closing with
    // Dr|Cr) computed from the account's normal balance, plus per-row
    // running_balance + balance_type — so we no longer derive Dr/Cr from a raw
    // sign here (which was wrong for credit-normal accounts and, together with a
    // field-name mismatch, produced the ₹NaN running balance). Fall back
    // defensively for any older payload shape.
    const opening = response.opening;
    const closing = response.closing;
    const openingBalRaw = response.openingBalance ?? 0;
    const closingBalRaw = response.summary?.closingBalance ?? 0;

    const rawEntries: any[] = response.transactions || response.entries || [];
    const entries = rawEntries.map((e) => {
      const run = toNum(e.running_balance ?? Math.abs(toNum(e.balance)));
      return {
        ...e,
        id: e.item_id ?? e.id ?? e.journal_entry_id,
        journal_entry_id: e.journal_entry_id ?? e.id,
        description: e.journal_description ?? e.description ?? '',
        line_description: e.description ?? '',
        debit_amount: toNum(e.debit_amount),
        credit_amount: toNum(e.credit_amount),
        running_balance: run,
        balance_type: (e.balance_type as 'Dr' | 'Cr') || (toNum(e.balance) >= 0 ? 'Dr' : 'Cr'),
        status: e.status || 'POSTED',
      };
    });

    return {
      account: {
        id: response.account?.id || accountId,
        account_code: response.account?.code || response.account?.account_code || '',
        account_name: response.account?.name || response.account?.account_name || '',
        account_type: response.account?.type || response.account?.account_type || 'ASSET',
        normalBalance: response.account?.normalBalance,
        parentName: response.account?.parentName ?? null,
        isActive: response.account?.isActive,
        currency: response.account?.currency || 'INR',
      },
      period: {
        fromDate: response.period?.fromDate || fromDate,
        toDate: response.period?.toDate || toDate,
        financialYear: response.period?.financialYear || '',
      },
      openingBalance: {
        amount: opening ? toNum(opening.amount) : Math.abs(toNum(openingBalRaw)),
        type: (opening?.type as 'Dr' | 'Cr') || (toNum(openingBalRaw) >= 0 ? 'Dr' : 'Cr'),
      },
      entries,
      closingBalance: {
        amount: closing ? toNum(closing.amount) : Math.abs(toNum(closingBalRaw)),
        type: (closing?.type as 'Dr' | 'Cr') || (toNum(closingBalRaw) >= 0 ? 'Dr' : 'Cr'),
      },
      totals: {
        totalDebit: toNum(response.summary?.totalDebit ?? response.totals?.totalDebit),
        totalCredit: toNum(response.summary?.totalCredit ?? response.totals?.totalCredit),
      },
      count: response.summary?.count ?? response.count ?? entries.length,
      netMovement: toNum(response.summary?.netMovement),
      netMovementType: response.summary?.netMovementType,
      lastTransactionDate: response.summary?.lastTransactionDate ?? null,
      monthly: response.monthly || [],
    };
  },

  getMultiAccountLedger: async (
    accountIds: number[],
    fromDate: string,
    toDate: string
  ): Promise<MultiAccountLedger> => {
    const response = await api.post<MultiAccountLedger>(
      '/finance/general-ledger/multi-account',
      { accountIds, fromDate, toDate }
    );
    return response;
  },

  getGeneralLedger: async (accountId: number, fromDate: string, toDate: string): Promise<any[]> => {
    const response = await api.get<{ ledger: any[]; count: number }>(
      `/finance/general-ledger/${accountId}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.ledger;
  },

  // =============================================
  // OPENING BALANCES
  // =============================================

  getOpeningBalances: async (financialYear: string): Promise<OpeningBalance[]> => {
    const response = await api.get<{ openingBalances: OpeningBalance[] }>(
      `/finance/opening-balances/${financialYear}`
    );
    return response.openingBalances;
  },

  setOpeningBalance: async (data: SetOpeningBalanceData): Promise<OpeningBalance> => {
    const response = await api.post<{ openingBalance: OpeningBalance }>(
      '/finance/opening-balances',
      data
    );
    return response.openingBalance;
  },

  // =============================================
  // FINANCIAL PERIODS
  // =============================================

  getFinancialPeriods: async (financialYear?: string): Promise<FinancialPeriod[]> => {
    const url = financialYear
      ? `/finance/financial-periods?financialYear=${financialYear}`
      : '/finance/financial-periods';
    const response = await api.get<{ periods: FinancialPeriod[] }>(url);
    return response.periods;
  },

  closeFinancialPeriod: async (periodId: number): Promise<FinancialPeriod> => {
    const response = await api.post<{ period: FinancialPeriod }>(
      `/finance/financial-periods/${periodId}/close`,
      {}
    );
    return response.period;
  },

  // =============================================
  // TRIAL BALANCE
  // =============================================

  getTrialBalance: async (asOnDate: string): Promise<TrialBalance> => {
    return api.get<TrialBalance>(`/finance/trial-balance?asOnDate=${asOnDate}`);
  },

  getComparativeTrialBalance: async (
    asOnDate: string,
    comparativeDate: string
  ): Promise<ComparativeTrialBalance> => {
    const raw = await api.get<any>(
      `/finance/reports/trial-balance/comparative?asOnDate=${asOnDate}&comparativeDate=${comparativeDate}`
    );
    return mapComparativeTB(raw);
  },

  // =============================================
  // SCHEDULE III REPORTS
  // =============================================

  getScheduleIIIBalanceSheet: async (
    asOnDate: string,
    comparativeDate?: string
  ): Promise<ScheduleIIIBalanceSheet> => {
    const params = new URLSearchParams();
    params.append('asOnDate', asOnDate);
    if (comparativeDate) params.append('comparativeDate', comparativeDate);

    return api.get<ScheduleIIIBalanceSheet>(
      `/finance/reports/balance-sheet/schedule-iii?${params.toString()}`
    );
  },

  getScheduleIIIProfitLoss: async (
    fromDate: string,
    toDate: string,
    comparativeFromDate?: string,
    comparativeToDate?: string
  ): Promise<ScheduleIIIProfitLoss> => {
    const params = new URLSearchParams();
    params.append('fromDate', fromDate);
    params.append('toDate', toDate);
    if (comparativeFromDate) params.append('comparativeFromDate', comparativeFromDate);
    if (comparativeToDate) params.append('comparativeToDate', comparativeToDate);

    return api.get<ScheduleIIIProfitLoss>(
      `/finance/reports/profit-loss/schedule-iii?${params.toString()}`
    );
  },

  getCashFlowStatement: async (fromDate: string, toDate: string): Promise<CashFlowStatement> => {
    return api.get<CashFlowStatement>(
      `/finance/reports/cash-flow?fromDate=${fromDate}&toDate=${toDate}`
    );
  },

  // =============================================
  // BASIC FINANCIAL REPORTS
  // =============================================

  getProfitLossReport: async (fromDate: string, toDate: string): Promise<any> => {
    return api.get<any>(`/finance/reports/profit-loss?fromDate=${fromDate}&toDate=${toDate}`);
  },

  getBalanceSheet: async (asOnDate: string): Promise<any> => {
    return api.get<any>(`/finance/reports/balance-sheet?asOnDate=${asOnDate}`);
  },

  // =============================================
  // GST REPORTS
  // =============================================

  getGSTR1Summary: async (returnPeriod: string): Promise<GSTR1Summary> => {
    const raw = await api.get<any>(`/finance/reports/gst/gstr1?returnPeriod=${returnPeriod}`);
    return mapGstr1Summary(raw, returnPeriod);
  },

  getGSTR1B2BDetails: async (returnPeriod: string): Promise<GSTR1B2BDetail[]> => {
    const response = await api.get<{ details: any[] }>(
      `/finance/reports/gst/gstr1/b2b?returnPeriod=${returnPeriod}`
    );
    return mapGstr1B2B(response.details || []);
  },

  getGSTR1HSNSummary: async (returnPeriod: string): Promise<GSTR1HSNItem[]> => {
    // Backend wraps as `data: { summary, count }` (not `hsnSummary`) — read the
    // right key, then map snake_case → the GSTR1HSNItem view-model.
    const response = await api.get<{ summary?: any[]; hsnSummary?: any[] }>(
      `/finance/reports/gst/gstr1/hsn?returnPeriod=${returnPeriod}`
    );
    return mapGstr1Hsn(response.summary || response.hsnSummary || []);
  },

  getGSTR3BSummary: async (returnPeriod: string): Promise<GSTR3BSummary> => {
    const raw = await api.get<any>(`/finance/reports/gst/gstr3b?returnPeriod=${returnPeriod}`);
    return mapGstr3bSummary(raw, returnPeriod);
  },

  getGSTInputOutputSummary: async (fromDate: string, toDate: string): Promise<GSTInputOutputSummary> => {
    const raw = await api.get<any>(`/finance/reports/gst/input-output?fromDate=${fromDate}&toDate=${toDate}`);
    return mapGstInputOutput(raw, fromDate, toDate);
  },

  recordGSTTransaction: async (data: RecordGSTTransactionData): Promise<GSTTransaction> => {
    const response = await api.post<{ transaction: GSTTransaction }>(
      '/finance/gst/transactions',
      data
    );
    return response.transaction;
  },

  // =============================================
  // TDS REPORTS
  // =============================================

  getTDSSummary: async (financialYear: string, quarter?: string): Promise<TDSSummary> => {
    const params = new URLSearchParams();
    params.append('financialYear', financialYear);
    if (quarter) params.append('quarter', quarter);

    const raw = await api.get<{ financialYear?: string; quarter?: string; summary?: any[] }>(
      `/finance/reports/tds/summary?${params.toString()}`
    );
    const rows = Array.isArray(raw?.summary) ? raw.summary : [];
    const sections = rows.map((r) => ({
      section: r.tds_section,
      sectionDescription: r.section_name || r.tds_section,
      deductionCount: Number(r.deduction_count) || 0,
      totalPaymentAmount: round2(r.total_gross),
      totalTDSDeducted: round2(r.total_deducted ?? r.total_tds),
      totalSurcharge: round2(r.total_surcharge),
      totalCess: round2(r.total_cess),
      totalDeposited: round2(r.total_deposited),
      totalPending: round2(r.total_pending),
    }));
    const totals = sections.reduce(
      (acc, s) => ({
        totalDeductions: acc.totalDeductions + s.deductionCount,
        totalPaymentAmount: round2(acc.totalPaymentAmount + s.totalPaymentAmount),
        totalTDSDeducted: round2(acc.totalTDSDeducted + s.totalTDSDeducted),
        totalDeposited: round2(acc.totalDeposited + s.totalDeposited),
        totalPending: round2(acc.totalPending + s.totalPending),
      }),
      { totalDeductions: 0, totalPaymentAmount: 0, totalTDSDeducted: 0, totalDeposited: 0, totalPending: 0 }
    );
    return { financialYear: raw?.financialYear || financialYear, quarter: quarter as TDSSummary['quarter'], sections, totals };
  },

  getTDSQuarterlyData: async (
    financialYear: string,
    quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4',
    formType?: 'Q26' | '27Q'
  ): Promise<TDSQuarterlyData> => {
    const params = new URLSearchParams();
    params.append('financialYear', financialYear);
    if (formType) params.append('formType', formType);

    const raw = await api.get<any>(`/finance/reports/tds/quarterly/${quarter}?${params.toString()}`);
    const rawDeductions: any[] = Array.isArray(raw?.deductions) ? raw.deductions : [];
    // Alias the SQL columns the 26Q table reads under the names it expects.
    const deductions = rawDeductions.map((d) => ({
      ...d,
      payment_date: d.payment_date || d.deduction_date,
      payment_amount: d.payment_amount ?? d.gross_amount,
      total_tds: d.total_tds ?? d.tds_amount,
    })) as TDSDeduction[];
    const totalDeducted = round2(deductions.reduce((s, d) => s + (Number(d.total_tds) || 0), 0));
    const totalDeposited = round2(
      rawDeductions.reduce((s, d) => s + (Number(d.is_deposited) === 1 ? Number(d.total_tds ?? d.tds_amount) || 0 : 0), 0)
    );
    const due = quarterDueDates(financialYear, quarter);
    return {
      financialYear: raw?.financialYear || financialYear,
      quarter,
      formType: (raw?.formType || formType || '26Q') as TDSQuarterlyData['formType'],
      deductorDetails: raw?.deductorDetails || { tan: '', name: '', address: '' },
      challans: (Array.isArray(raw?.challanSummary) ? raw.challanSummary : raw?.challans) || [],
      deductions,
      summary: {
        totalTDSDeducted: totalDeducted,
        totalTDSDeposited: totalDeposited,
        interest: 0,
        lateFee: 0,
      },
      ...due,
    };
  },

  getTDSDeductedReport: async (fromDate: string, toDate: string): Promise<TDSDeduction[]> => {
    const response = await api.get<{ deductions: any[] }>(
      `/finance/reports/tds/deducted?fromDate=${fromDate}&toDate=${toDate}`
    );
    return (response.deductions || []).map((d) => ({
      ...d,
      payment_date: d.payment_date || d.deduction_date,
      payment_amount: d.payment_amount ?? d.gross_amount,
      total_tds: d.total_tds ?? d.tds_amount,
    })) as TDSDeduction[];
  },

  getTDSPayableReport: async (financialYear: string, quarter: string): Promise<TDSPayableReport> => {
    // Built from the per-deductee "deducted" data (filtered to not-yet-deposited)
    // because the /payable endpoint returns only section-level aggregates, while
    // the Payable page renders a per-deductee overdue table with interest.
    const { from, to } = quarterRange(financialYear, quarter);
    const raw = await api.get<{ deductions: any[] }>(
      `/finance/reports/tds/deducted?fromDate=${from}&toDate=${to}`
    );
    const pending = (raw.deductions || []).filter((d) => Number(d.is_deposited) === 0);
    const now = new Date();
    const items = pending.map((d) => {
      const tdsAmount = round2(d.total_tds ?? d.tds_amount);
      const due = depositDueForDeduction(d.deduction_date || d.payment_date);
      const daysOverdue = Math.max(0, Math.floor((now.getTime() - due.getTime()) / 86400000));
      const monthsLate = daysOverdue > 0 ? Math.ceil(daysOverdue / 30) : 0;
      // Interest u/s 201(1A): 1.5% per month (or part) on late deposit.
      const interestPayable = round2(tdsAmount * 0.015 * monthsLate);
      return {
        section: d.tds_section,
        deducteeName: d.deductee_name,
        deducteePan: d.deductee_pan || '',
        paymentDate: d.deduction_date || d.payment_date,
        paymentAmount: round2(d.gross_amount ?? d.payment_amount),
        tdsAmount,
        depositDueDate: fmtDMY(due),
        daysOverdue,
        interestPayable,
      };
    });
    const totals = items.reduce(
      (acc, i) => ({
        totalTDSPayable: round2(acc.totalTDSPayable + i.tdsAmount),
        totalInterest: round2(acc.totalInterest + i.interestPayable),
      }),
      { totalTDSPayable: 0, totalInterest: 0 }
    );
    return { financialYear, quarter: quarter as TDSPayableReport['quarter'], items, totals };
  },

  getTDSChallanList: async (financialYear: string): Promise<TDSChallan[]> => {
    const response = await api.get<{ challans: any[] }>(
      `/finance/reports/tds/challans?financialYear=${financialYear}`
    );
    return (response.challans || []).map((c) => ({
      ...c,
      deposit_amount: c.deposit_amount ?? c.total_amount,
    })) as TDSChallan[];
  },

  recordTDSDeduction: async (data: RecordTDSDeductionData): Promise<TDSDeduction> => {
    const response = await api.post<{ deduction: TDSDeduction }>(
      '/finance/tds/deductions',
      data
    );
    return response.deduction;
  },

  recordTDSChallan: async (data: RecordTDSChallanData): Promise<TDSChallan> => {
    const response = await api.post<{ challan: TDSChallan }>(
      '/finance/tds/challans',
      data
    );
    return response.challan;
  },

  // =============================================
  // AGEING REPORTS
  // =============================================

  getReceivablesAgeing: async (asOnDate: string): Promise<AgeingReport> => {
    const raw = await api.get<any>(`/finance/reports/ageing/receivables?asOnDate=${asOnDate}`);
    return mapAgeing(raw, 'RECEIVABLES');
  },

  getPayablesAgeing: async (asOnDate: string): Promise<AgeingReport> => {
    const raw = await api.get<any>(`/finance/reports/ageing/payables?asOnDate=${asOnDate}`);
    return mapAgeing(raw, 'PAYABLES');
  },

  // =============================================
  // DAY BOOK & COST CENTER
  // =============================================

  getDayBook: async (date: string): Promise<DayBook> => {
    // The API returns entries in camelCase with a `lines` array; the Day Book
    // page reads snake_case entries with an `accounts` array and per-entry
    // totals. Normalise here so the page renders (and never hits
    // `entry.accounts.map` on undefined). Totals already come back camelCase.
    const raw: any = await api.get(`/finance/reports/day-book?date=${date}`);
    const entries = (raw?.entries || []).map((e: any) => {
      const accounts = (e.lines || e.accounts || []).map((l: any) => ({
        account_code: l.accountCode ?? l.account_code ?? '',
        account_name: l.accountName ?? l.account_name ?? '',
        debit_amount: Number(l.debit ?? l.debit_amount) || 0,
        credit_amount: Number(l.credit ?? l.credit_amount) || 0,
      }));
      return {
        id: e.id,
        entry_number: e.entryNumber ?? e.entry_number ?? '',
        entry_date: e.entryDate ?? e.entry_date,
        description: e.description,
        reference: e.reference,
        status: e.status,
        created_by_name: e.createdBy ?? e.created_by_name ?? '',
        total_debit: accounts.reduce((s: number, a: any) => s + a.debit_amount, 0),
        total_credit: accounts.reduce((s: number, a: any) => s + a.credit_amount, 0),
        accounts,
      };
    });
    return { ...raw, entries } as DayBook;
  },

  getCostCenterReport: async (
    costCenterId: number,
    fromDate: string,
    toDate: string
  ): Promise<CostCenterReport> => {
    return api.get<CostCenterReport>(
      `/finance/reports/cost-center/${costCenterId}?fromDate=${fromDate}&toDate=${toDate}`
    );
  },

  // =============================================
  // BANK RECONCILIATION
  // =============================================

  // Unlike the list endpoint, this controller's `data` IS the reconciliation
  // object itself, not nested under a `reconciliation` key (verified live:
  // POST /finance/bank-reconciliation returns `{status,message,data:{id,...}}`)
  // — see financeController.createBankReconciliation.
  createBankReconciliation: async (data: CreateBankReconciliationData): Promise<BankReconciliation> => {
    return normalizeRecon(await api.post<BankReconciliation>('/finance/bank-reconciliation', data));
  },

  getBankReconciliations: async (bankAccountId?: number): Promise<BankReconciliation[]> => {
    const url = bankAccountId
      ? `/finance/bank-reconciliation?bankAccountId=${bankAccountId}`
      : '/finance/bank-reconciliation';
    const response = await api.get<{ reconciliations: BankReconciliation[] }>(url);
    return response.reconciliations;
  },

  // NOTE: unused anywhere in the app today, but also flat like create/complete
  // above (see financeController.getBankReconciliationById) — kept nested
  // here on purpose is wrong; left as-is per audit scope (no live caller to
  // break).
  getBankReconciliationById: async (id: number): Promise<BankReconciliation> => {
    const response = await api.get<{ reconciliation: BankReconciliation }>(
      `/finance/bank-reconciliation/${id}`
    );
    return response.reconciliation;
  },

  reconcileItem: async (
    reconciliationId: number,
    itemId: number,
    status: 'MATCHED' | 'UNMATCHED' | 'ADJUSTED',
    notes?: string
  ): Promise<BankReconciliation> => {
    // The controller's `data` is the reconciliation object itself, not
    // nested under a `reconciliation` key (unlike most other endpoints) —
    // see Finance.reconcileItem / financeController.reconcileItem.
    const response = await api.patch<BankReconciliation>(
      `/finance/bank-reconciliation/${reconciliationId}/items/${itemId}`,
      { status, notes }
    );
    return normalizeRecon(response);
  },

  // Also flat, same as createBankReconciliation above.
  completeBankReconciliation: async (id: number): Promise<BankReconciliation> => {
    return normalizeRecon(await api.post<BankReconciliation>(`/finance/bank-reconciliation/${id}/complete`, {}));
  },

  // =============================================
  // EXPORT REPORTS
  // =============================================

  exportReportPDF: async (data: ExportReportData): Promise<Blob> => {
    const response = await api.post<Blob>('/finance/reports/export/pdf', data, {
      responseType: 'blob'
    });
    return response;
  },

  exportReportExcel: async (data: ExportReportData): Promise<Blob> => {
    const response = await api.post<Blob>('/finance/reports/export/excel', data, {
      responseType: 'blob'
    });
    return response;
  },

  // =============================================
  // BUDGET (Placeholder)
  // =============================================

  getBudget: async (): Promise<any> => {
    return api.get<any>('/finance/budget');
  },

  // =============================================
  // LEGACY METHODS (for backward compatibility)
  // =============================================

  getTransactions: async (params?: any): Promise<Transaction[]> => {
    return api.get<Transaction[]>('/finance/transactions', { params });
  },

  createTransaction: async (transaction: Omit<Transaction, 'id'>): Promise<Transaction> => {
    return api.post<Transaction>('/finance/transactions', transaction);
  },

  updateTransaction: async (id: string, transaction: Partial<Transaction>): Promise<Transaction> => {
    return api.put<Transaction>(`/finance/transactions/${id}`, transaction);
  },

  deleteTransaction: async (id: string): Promise<void> => {
    return api.delete(`/finance/transactions/${id}`);
  },

  getInvoices: async (params?: any): Promise<Invoice[]> => {
    return api.get<Invoice[]>('/finance/invoices', { params });
  },

  getInvoice: async (id: string): Promise<Invoice> => {
    return api.get<Invoice>(`/finance/invoices/${id}`);
  },

  createInvoice: async (invoice: Omit<Invoice, 'id'>): Promise<Invoice> => {
    return api.post<Invoice>('/finance/invoices', invoice);
  },

  updateInvoice: async (id: string, invoice: Partial<Invoice>): Promise<Invoice> => {
    return api.put<Invoice>(`/finance/invoices/${id}`, invoice);
  },

  deleteInvoice: async (id: string): Promise<void> => {
    return api.delete(`/finance/invoices/${id}`);
  },

  getFinancialReport: async (period: string): Promise<FinancialReport> => {
    return api.get<FinancialReport>(`/finance/reports/${period}`);
  },

  getCashFlowReport: async (startDate: string, endDate: string): Promise<any> => {
    return api.get('/finance/reports/cashflow', {
      params: { startDate, endDate },
    });
  },
};
